import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { FaChevronDown, FaChevronUp, FaTimes, FaCheckCircle, FaMusic } from 'react-icons/fa'
import { dismissJob, dismissAllJobs, markJobViewed, toggleQueueDrawer, getToolMetadata } from '../../redux/reducers/jobsReducer'
import { updateGenerationSession } from '../../redux/reducers/generationSessionReducer'
import { resetUploadedFile } from '../../redux/reducers/uploadFileReducer'
import { useResponsive } from '../../hooks'
import { media } from '../../utils/media'
import { QUEUE_NOTIFICATION_CONFIG, getRenamedToolName, resolveToolId, MODERATION_PAGE, VOICE_CLONE, QUEUE_NOTIFICATION } from '../../utils/constants'
import isAuthRoute from '../../utils/helper'

const getFromFlowForTool = (tool_name) => {
  switch (tool_name) {
    case 'text_to_image':
      return 'textToImage'
    case 'text_to_video':
      return 'textToVideo'
    case 'image_to_video':
      return 'imageToVideo'
    case 'face_swap':
    case 'video_face_swap':
      return 'faceswap'
    case 'background_change':
    case 'scene_swap':
      return 'background'
    case 'colorize':
    case 'color_mode_enhance':
      return 'colorize'
    case 'voice_remix':
      return 'voiceRemix'
    case 'action_generation':
      return 'actionGeneration'
    case 'motion_control':
      return 'motionControl'
    case 'talking_avatar':
    case 'photo_avatar_talking':
      return 'talkingAvatar'
    case 'voice_cloning':
    case 'voice_clone':
    case 'voiceclone':
    case 'text_to_audio':
    case 'textToAudio':
      return 'textToAudio'
    default:
      return tool_name || ''
  }
}

const getRelativeTime = (timestamp) => {
  if (!timestamp) return QUEUE_NOTIFICATION_CONFIG.JUST_NOW
  const diffSec = Math.floor((Date.now() - timestamp) / 1000)
  if (diffSec < 45) return QUEUE_NOTIFICATION_CONFIG.JUST_NOW
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  return `${Math.floor(diffSec / 3600)}h ago`
}

const isJobFailedOrModerated = (job) => {
  if (!job) return false
  const status = String(job.status || '').toUpperCase()
  if (status === 'COMPLETED' || job.status === 'completed') {
    return false
  }
  const pStatus = String(job.process_status || '').toUpperCase()
  const jStatus = String(job.job_status || '').toUpperCase()
  const errText = `${job.error || ''} ${job.statusMessage || ''} ${job.error_message || ''}`

  const isPostgenNsfw =
    status === 'CHECK_POSTGEN_NSFW' ||
    pStatus === 'CHECK_POSTGEN_NSFW' ||
    jStatus === 'CHECK_POSTGEN_NSFW' ||
    /check[_\s]postgen[_\s]nsfw/i.test(status) ||
    /check[_\s]postgen[_\s]nsfw/i.test(pStatus) ||
    /check[_\s]postgen[_\s]nsfw/i.test(jStatus) ||
    /check[_\s]postgen[_\s]nsfw/i.test(errText)

  if (isPostgenNsfw) {
    return false
  }

  const cleanedErrText = errText.replace(/check[_\s]postgen[_\s]nsfw/gi, '')

  return (
    status === 'FAILED' ||
    status === 'REJECTED' ||
    status === 'MODERATION_REJECTED' ||
    status === 'MODERATION_PENDING' ||
    status === 'MODERATION_PENDING_REVIEW' ||
    status === 'MODERATION' ||
    pStatus === 'FAILED' ||
    pStatus === 'REJECTED' ||
    pStatus === 'MODERATION_REJECTED' ||
    pStatus === 'MODERATION_PENDING' ||
    pStatus === 'MODERATION_PENDING_REVIEW' ||
    jStatus === 'FAILED' ||
    jStatus === 'REJECTED' ||
    jStatus === 'MODERATION_REJECTED' ||
    jStatus === 'MODERATION_PENDING' ||
    jStatus === 'MODERATION_PENDING_REVIEW' ||
    job.is_rejected === true ||
    job.moderation_rejected === true ||
    job.is_moderation === true ||
    Boolean(job.error) ||
    /nsfw|moderation|reject|inappropriate|restricted/i.test(cleanedErrText)
  )
}

export const QueueNotificationWidget = () => {
  const isMobile = useResponsive(media.mobile)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated ?? Boolean(localStorage.getItem('access')))
  const activeJobs = useSelector((state) => state.jobs?.activeJobs || {})
  const isExpanded = useSelector((state) => state.jobs?.isDrawerExpanded || false)

  // Dragging state anchored to bottom & right
  const [position, setPosition] = React.useState(null)
  const widgetRef = React.useRef(null)
  const dragInfoRef = React.useRef({ startX: 0, startY: 0, initialRight: 0, initialBottom: 0, isDragging: false })

  if (!isAuthenticated || isAuthRoute(location.pathname)) {
    return null
  }

  const handlePointerDown = (e) => {
    // Only drag with primary click and ignore clicks on buttons / links
    if (e.button !== 0 || e.target.closest('button') || e.target.closest('a')) return
    e.preventDefault()

    const rect = widgetRef.current?.getBoundingClientRect()
    if (!rect) return

    dragInfoRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialRight: window.innerWidth - rect.right,
      initialBottom: window.innerHeight - rect.bottom,
      isDragging: true
    }

    const handlePointerMove = (moveEvt) => {
      if (!dragInfoRef.current.isDragging) return
      const deltaX = moveEvt.clientX - dragInfoRef.current.startX
      const deltaY = moveEvt.clientY - dragInfoRef.current.startY

      const clampedRight = Math.max(10, Math.min(window.innerWidth - (rect.width || 340) - 10, dragInfoRef.current.initialRight - deltaX))
      const clampedBottom = Math.max(10, Math.min(window.innerHeight - 80, dragInfoRef.current.initialBottom - deltaY))

      setPosition({ right: clampedRight, bottom: clampedBottom })
    }

    const handlePointerUp = () => {
      dragInfoRef.current.isDragging = false
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
  }

  // Filter non-dismissed jobs and deduplicate by generation ID / job ID / tool_name
  const rawJobs = Object.values(activeJobs)
    .filter((job) => !job.dismissed)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))

  const visibleJobsMap = new Map()
  rawJobs.forEach((job) => {
    const meta = getToolMetadata(job.tool_name)
    const key = job.generationId || job.jobId || job.id || meta.tool_name
    if (!key) return
    if (!visibleJobsMap.has(key)) {
      visibleJobsMap.set(key, job)
    } else {
      const existing = visibleJobsMap.get(key)
      if (existing.status !== 'completed' && job.status === 'completed') {
        visibleJobsMap.set(key, job)
      } else if (job.status === 'completed' && Boolean(job.output_url) && !existing.output_url) {
        visibleJobsMap.set(key, job)
      } else if ((job.progress || 0) > (existing.progress || 0)) {
        visibleJobsMap.set(key, job)
      }
    }
  })
  const visibleJobs = Array.from(visibleJobsMap.values())

  const count = visibleJobs.length

  if (count === 0) return null

  const handleToggle = () => {
    dispatch(toggleQueueDrawer())
  }

  const handleDismiss = (e, jobId) => {
    e.stopPropagation()
    dispatch(markJobViewed(jobId))
    dispatch(dismissJob({ jobId, force: true }))
  }

  const handleDismissAll = (e) => {
    e.stopPropagation()
    dispatch(dismissAllJobs())
  }

  const handleNavigate = (e, job, targetUrl, isFinished = false) => {
    if (e) e.stopPropagation()
    const jobId = job?.jobId || job?.generationId || job?.id
    const meta = getToolMetadata(job?.tool_name)
    const fromFlow = getFromFlowForTool(meta?.tool_name || job?.tool_name)
    const isFailed = isJobFailedOrModerated(job)
    const targetToolId =
      resolveToolId(job?.tool_name) ||
      resolveToolId(job?.ai_tool_id) ||
      resolveToolId(meta?.tool_name) ||
      null
    const jobInputUrl =
      job?.input_video_url ||
      job?.meta?.videoPreview ||
      job?.input_file_url ||
      job?.meta?.input_file_url ||
      job?.input_image_url ||
      job?.meta?.input_image_url ||
      null
    const jobThumbnailUrl =
      job?.meta?.videoThumbnail ||
      job?.thumbnail_url ||
      jobInputUrl ||
      null

    dispatch(resetUploadedFile())

    const isVideoJob =
      job?.mediaType === 'video' ||
      meta?.mediaType === 'video' ||
      job?.meta?.content_type === 'video' ||
      job?.tool_name === 'video_face_swap' ||
      (typeof job?.output_url === 'string' &&
        (job.output_url.includes('.mp4') ||
          job.output_url.includes('.m3u8') ||
          job.output_url.includes('.webm') ||
          job.output_url.includes('.mov') ||
          job.output_url.includes('/video')))
    const isAudioJob =
      job?.mediaType === 'audio' ||
      meta?.mediaType === 'audio' ||
      (typeof job?.output_url === 'string' &&
        Boolean(job.output_url.match(/\.(mp3|wav|ogg|m4a)(\?.*)?$/i)))
    const resolvedContentType = isVideoJob ? 'video' : isAudioJob ? 'audio' : 'image'

    if (jobId && isFinished && !isFailed) {
      dispatch(markJobViewed(jobId))
      dispatch(dismissJob({ jobId, force: true }))
      if (job?.output_url) {
        dispatch(
          updateGenerationSession({
            output_url: job.output_url,
            content_type: resolvedContentType,
            generation_id: jobId,
            file_url: jobInputUrl,
            thumbnail_url: jobThumbnailUrl,
            content_id: job.content_id || jobId,
            prompt: job.prompt || null,
            ai_tool_id: targetToolId
          })
        )
      }
    } else if (jobId && !isFinished) {
      dispatch(
        updateGenerationSession({
          output_url: null,
          content_type: resolvedContentType,
          generation_id: jobId,
          file_url: jobInputUrl,
          thumbnail_url: jobThumbnailUrl,
          content_id: job?.content_id || jobId,
          prompt: job?.prompt || null,
          ai_tool_id: targetToolId
        })
      )
    }
    if (targetUrl) {
      let destUrl = targetUrl
      if (jobId && !targetUrl.includes('job_id=')) {
        const separator = targetUrl.includes('?') ? '&' : '?'
        destUrl = `${targetUrl}${separator}job_id=${jobId}`
      }
      navigate(destUrl, {
        state: {
          from: fromFlow,
          childId: jobId,
          showOutput: isFinished && !isFailed,
          showFailedError: isFailed,
          failedError: job?.error || job?.statusMessage || job?.error_message || 'Content moderation review required',
          audio: job?.meta?.selectedAudio || null,
          prompt: job?.prompt || job?.meta?.prompt || null,
          output_url: job?.output_url,
          outputUrl: job?.output_url,
          contentType: resolvedContentType,
          media_type: resolvedContentType
        }
      })
    }
  }

  const handleCardClick = (e, job, targetUrl, isFinished = false) => {
    if (e?.target?.closest('button')) return
    handleNavigate(e, job, targetUrl, isFinished)
  }

  // Show top header pill ONLY if count >= 2
  const showPillHeader = count >= 2

  // When count >= 2: if collapsed, show only latest 1 card; if expanded, show all cards.
  // When count === 1: show the single card directly (no pill header).
  const displayedJobs = showPillHeader ? (isExpanded ? visibleJobs : visibleJobs.slice(0, 1)) : visibleJobs

  const widgetStyles = position
    ? { right: `${position.right}px`, bottom: `${position.bottom}px`, left: 'auto', top: 'auto' }
    : isMobile
    ? { bottom: '70px', right: '12px', left: 'auto', top: 'auto' }
    : undefined

  return (
    <>
      <style>{`
        .queue-widget-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .queue-widget-scroll::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.8);
          border-radius: 9999px;
        }
        .queue-widget-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.6);
          border-radius: 9999px;
        }
        .queue-widget-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(203, 213, 225, 0.9);
        }
      `}</style>
      <div
        ref={widgetRef}
        style={widgetStyles}
        className={`fixed ${!position && !isMobile ? 'bottom-6 right-6' : ''} z-[9999] flex flex-col items-end font-sans select-none`}
      >
        {/* Header Toggle Pill & Drag Handle (ONLY rendered if count >= 2) */}
        {showPillHeader && (
          <div
            onPointerDown={handlePointerDown}
            className="flex items-center bg-[#0F172A]/95 backdrop-blur-md border border-white/15 text-white rounded-t-xl border-b-0 text-xs font-semibold shadow-xl transition-all duration-200 overflow-hidden cursor-grab active:cursor-grabbing"
            title="Drag to reposition widget"
          >
            <div className="pl-2.5 pr-1 py-1.5 flex items-center text-slate-400 opacity-70">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="9" cy="6" r="2" />
                <circle cx="15" cy="6" r="2" />
                <circle cx="9" cy="12" r="2" />
                <circle cx="15" cy="12" r="2" />
                <circle cx="9" cy="18" r="2" />
                <circle cx="15" cy="18" r="2" />
              </svg>
            </div>
            <button
              onClick={handleToggle}
              className="flex items-center gap-2 px-2.5 py-1.5 hover:border-[#00B8F8]/50 text-white cursor-pointer bg-transparent border-0 text-xs font-semibold"
            >
              <span>{`${count} ${QUEUE_NOTIFICATION.TITLE_QUEUING_IN_PROCESS}`}</span>
              {isExpanded ? (
                <FaChevronDown className="text-xs text-[#00B8F8]" />
              ) : (
                <FaChevronUp className="text-[#00B8F8] text-xs" />
              )}
            </button>
            <button
              onClick={handleDismissAll}
              title={QUEUE_NOTIFICATION.CLOSE_ALL}
              className="px-2 py-1.5 text-slate-400 hover:text-white transition-colors cursor-pointer text-xs bg-transparent border-0 border-l border-white/10"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Cards Container sitting directly BELOW header pill */}
        <div
          className={`${
            isMobile
              ? 'w-[calc(100vw-24px)] max-w-[360px] max-h-[215px]'
              : 'w-[360px] max-h-[380px]'
          } flex flex-col gap-2.5 overflow-y-auto transition-all duration-300 queue-widget-scroll`}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(148, 163, 184, 0.6) rgba(30, 41, 59, 0.8)'
          }}
        >
        {displayedJobs.map((job, index) => {
          const isCompleted = job.status === 'completed'
          const isFailed = isJobFailedOrModerated(job)
          const isProcessing = !isCompleted && !isFailed

          // Title formatting
          const meta = getToolMetadata(job.tool_name)
          const rawToolTitle = (job.display_name && job.display_name !== 'AI Tool') ? job.display_name : meta.display_name
          console.log('job?.tool_name.toUpperCase()', job?.tool_name.toUpperCase())
          const toolTitle =
            MODERATION_PAGE?.TOOL_NAMES?.[job?.tool_name.toUpperCase()] ||
            getRenamedToolName(job.tool_name) ||
            getRenamedToolName(job.ai_tool_id) ||
            getRenamedToolName(rawToolTitle) ||
            rawToolTitle ||
            'AI Tool'
          let cardTitle = `${toolTitle} ${QUEUE_NOTIFICATION?.READY}!`
          if (isFailed) cardTitle = QUEUE_NOTIFICATION.PROCESSING_FAILED
          else if (isProcessing) cardTitle = `${MODERATION_PAGE?.GENERATING} ${toolTitle}...`

          // Action link label
          const isVideo =
            job.mediaType === 'video' ||
            meta?.mediaType === 'video' ||
            job?.meta?.content_type === 'video' ||
            job?.tool_name === 'video_face_swap' ||
            (typeof job?.output_url === 'string' &&
              (job.output_url.includes('.mp4') ||
                job.output_url.includes('.m3u8') ||
                job.output_url.includes('.webm') ||
                job.output_url.includes('.mov') ||
                job.output_url.includes('/video')))
          const isAudio =
            job.mediaType === 'audio' ||
            meta?.mediaType === 'audio' ||
            (typeof job?.output_url === 'string' &&
              Boolean(job.output_url.match(/\.(mp3|wav|ogg|m4a)(\?.*)?$/i)))
          const actionText = isVideo ? QUEUE_NOTIFICATION.VIEW_VIDEO : isAudio ? QUEUE_NOTIFICATION.VIEW_AUDIO : QUEUE_NOTIFICATION.VIEW_IMAGE

          // Border color class based on status
          const borderClass = isCompleted
            ? 'border-l-4 border-l-[#10B981]'
            : isFailed
            ? 'border-l-4 border-l-[#EF4444]'
            : 'border-l-4 border-l-[#00B8F8]'

          const isMulti = (job.childIds && job.childIds.length > 1) || (job.outputs && job.outputs.length > 1)
          const totalOutputs = job.childIds?.length || job.outputs?.length || 1
          const thumbUrl = (job.thumbnail_url && !job.thumbnail_url.startsWith('blob:')) ? job.thumbnail_url : (job.output_url && !job.output_url.startsWith('blob:')) ? job.output_url : null
          const targetJobId = job.jobId || job.generationId || job.id
          const fromFlow = getFromFlowForTool(meta.tool_name || job.tool_name)
          const mediaTypeParam = isVideo ? 'video' : isAudio ? 'audio' : 'image'
          const mwebOutputUrl = `/output?job_id=${targetJobId}&output_url=${encodeURIComponent(job.output_url || '')}&from=${fromFlow}&media_type=${mediaTypeParam}`
          const targetUrl =
            isMobile && isCompleted && job.output_url && !isMulti
              ? mwebOutputUrl
              : job.targetUrl ||
                (job.output_url && !isMulti
                  ? `${meta.route}?job_id=${targetJobId}&output_url=${encodeURIComponent(job.output_url)}`
                  : `${meta.route}?job_id=${targetJobId}`)

          return (
            <div
              key={targetJobId || `job_${index}`}
              onPointerDown={handlePointerDown}
              onClick={(e) => handleCardClick(e, job, targetUrl, isCompleted || isFailed)}
              className={`relative flex gap-3 bg-[#0F172A]/95 backdrop-blur-md border border-white/15 ${borderClass} rounded-xl p-4 shadow-2xl transition-all duration-200 hover:border-white/25 cursor-pointer text-left`}
            >
              {/* Thumbnail Preview */}
              <div className="relative w-28 h-20 min-w-[56px] rounded-xl overflow-hidden bg-[#1E293B] flex items-center justify-center shrink-0">
                {thumbUrl ? (
                  isVideo && job.output_url ? (
                    <video src={job.output_url} muted loop className="w-full h-full object-contain" />
                  ) : (
                    <img src={thumbUrl} alt="Preview" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none' }} />
                  )
                ) : isVideo ? (
                  <svg className="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="6 3 20 12 6 21 6 3" />
                  </svg>
                ) : isAudio ? (
                  <FaMusic className="text-white text-lg" />
                ) : (
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 18l6-10 4 6 3-4 5 8H3z" />
                  </svg>
                )}

                {isProcessing && (
                  <div className="absolute bottom-1 left-1 w-4 h-4 rounded-full bg-[#0F172A]/70 backdrop-blur-sm flex items-center justify-center">
                    <svg className="animate-spin w-3 h-3 text-[#00B8F8]" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                )}
                {isCompleted && (
                  <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-[#10B981] text-white text-[10px] font-bold flex items-center justify-center border border-[#0F172A] shadow-sm">
                    ✓
                  </div>
                )}
                {isFailed && (
                  <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-[#EF4444] text-white flex items-center justify-center border border-[#0F172A] shadow-sm">
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L1 21h22L12 2zm0 4l7.5 13h-15L12 6zm-1 5h2v4h-2zm0 5h2v2h-2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content Column */}
              <div className="flex-1 flex flex-col justify-center items-start text-left gap-1 min-w-0">
                <div className="flex justify-between items-center w-full gap-2">
                  <h4 className="text-sm font-bold text-white truncate text-left leading-snug">{cardTitle}</h4>
                  <button
                    onClick={(e) => handleDismiss(e, targetJobId)}
                    title={QUEUE_NOTIFICATION.DISMISS}
                    className="text-slate-400 hover:text-white transition-colors cursor-pointer p-0.5 text-xs shrink-0 bg-transparent border-0"
                  >
                    <FaTimes />
                  </button>
                </div>

                {/* Status subtitle */}
                <div className="text-xs font-medium text-left w-full">
                  {isCompleted && (
                    <div className="text-[#10B981] flex items-center gap-1.5 text-left">
                      <FaCheckCircle className="text-xs shrink-0" />
                      <span className="text-left">{isMulti ? QUEUE_NOTIFICATION?.ALL_OUTPUTS_READY.replace('%', totalOutputs) : QUEUE_NOTIFICATION.PROCESSING_COMPLETE}</span>
                    </div>
                  )}
                  {isFailed && (
                    <div className="text-[#EF4444] flex items-center gap-1.5 text-left">
                      <div className="w-3.5 h-3.5 rounded-full border border-[#EF4444] flex items-center justify-center text-[10px] font-bold shrink-0">i</div>
                      <span className="truncate text-left">{job.error || QUEUE_NOTIFICATION.FALLBACK_ERROR}</span>
                    </div>
                  )}
                  {isProcessing && (
                    <div className="text-slate-300 truncate text-left w-full block">
                      {job.statusMessage || `Generating your ${meta.mediaType || 'creation'}...`}
                    </div>
                  )}
                </div>

                {/* Progress bar for processing jobs */}
                {isProcessing && (
                  <div className="w-full h-1.5 bg-white/10 rounded-full mt-1.5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#00B8F8] to-[#00E5FF] rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.max(0, Math.min(99, Math.round(job.progress ?? 0)))}%` }}
                    />
                  </div>
                )}

                {/* Footer Actions & Progress / Timestamp */}
                <div className="flex justify-between items-center w-full mt-1 pt-0.5">
                  {isCompleted && (
                    <button
                      onClick={(e) => handleNavigate(e, job, targetUrl, true)}
                      className="text-xs font-semibold text-[#10B981] hover:underline hover:opacity-85 transition-all cursor-pointer bg-transparent border-0 p-0 flex items-center gap-1 text-left"
                    >
                      {actionText}
                    </button>
                  )}
                  {isFailed && (
                    <div className="flex items-center gap-3 text-left">
                      <button
                        onClick={(e) => handleDismiss(e, targetJobId)}
                        className="text-xs font-semibold text-[#EF4444] hover:underline hover:opacity-85 transition-all cursor-pointer bg-transparent border-0 p-0 text-left"
                      >
                        {QUEUE_NOTIFICATION.DISMISS}
                      </button>
                    </div>
                  )}
                  {isProcessing && (
                    <span className="text-xs font-semibold text-[#00B8F8] text-left">
                      {`${Math.max(0, Math.min(99, Math.round(job.progress ?? 0)))}% ${VOICE_CLONE?.STATUS?.COMPLETED}`} 
                    </span>
                  )}
                  {(isCompleted || isFailed) && (
                    <span className="text-[11px] text-slate-400 text-right">{getRelativeTime(job.updatedAt || job.createdAt)}</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  </>
)
}

export default QueueNotificationWidget
